<template>
    <v-app>
        <div class="demo-wrapper">
            <backdrop-demo />

            <transition name="fade">
                <div>
                    <div
                        v-if="!submitted && !thank_you_shown"
                        class="form-container text-center"
                    >
                        <v-row>
                            <v-col
                                cols="5"
                                class="side-by-side-image-wrapper"
                                v-if="$vuetify.breakpoint.lgAndUp"
                            >
                                <img
                                    src="/app/guest-checkin-images/wine-glass-pour.jpg"
                                    class="side-by-side-image"
                                    alt=""
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                lg="7"
                            >
                                <branding-header />

                                <h3 class="heading">Welcome to Barrel Creek Vineyards!</h3>
                                <h4 class="subheading">Check in to get your visit started</h4>

                                <form-demo
                                    ref="form"
                                    v-model="form_data"
                                    @submit="submitForm"
                                >
                                    <template #email>
                                        <v-text-field
                                            v-model="form_data.email"
                                            label="Email Address"
                                            type="email"
                                            autocomplete="off"
                                            outlined
                                            prepend-inner-icon="mdi-email"
                                            validate-on-blur
                                            class="ma-0"
                                            :class="[!form_data.email && 'mb-10']"
                                            required
                                            :rules="[rules.required, rules.email_verification]"
                                            hide-details
                                        />

                                        <transition name="fade">
                                            <div v-if="form_data.email">
                                                <v-checkbox
                                                    v-model="form_data.email_consent"
                                                    class="mt-3 mb-8"
                                                    color="primary"
                                                    hide-details
                                                >
                                                    <template #label>
                                                        <span class="disclaimer text-left">
                                                            I give Barrel Creek Vineyards permission to email me with
                                                            info and
                                                            offers. Use is subject to <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                            >Terms</a>
                                                            with an arbitration clause and a <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                            >Privacy Policy</a>.
                                                        </span>
                                                    </template>
                                                </v-checkbox>
                                            </div>
                                        </transition>
                                    </template>

                                    <template #phone>
                                        <v-text-field
                                            v-model="form_data.phone"
                                            hide-details
                                            label="Mobile Phone Number (optional)"
                                            type="tel"
                                            autocomplete="off"
                                            outlined
                                            prepend-inner-icon="mdi-cellphone"
                                            validate-on-blur
                                            class="mt-2 pt-0"
                                            @input="onInputPhone"
                                            :rules="[rules.optional_phone_verification]"
                                        />

                                        <transition name="fade">
                                            <div v-if="form_data.phone">
                                                <v-checkbox
                                                    v-model="form_data.phone_consent"
                                                    class="mt-3"
                                                    color="primary"
                                                    hide-details
                                                >
                                                    <template #label>
                                                        <span class="disclaimer text-left">
                                                            I give Barrel Creek Vineyards permission to send email and
                                                            text
                                                            messages
                                                            with info,
                                                            promotions, and offers to the number I have provided,
                                                            including use
                                                            of
                                                            an
                                                            automated system.
                                                            Consent is not a condition of purchase. Texts are recorded.
                                                            Message
                                                            and/or
                                                            data rates may apply.
                                                            Message frequency varies. Text STOP to opt out or HELP for
                                                            help at
                                                            any
                                                            time.
                                                            Use is subject to <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                            >Terms</a>
                                                            with an arbitration clause and a <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                            >Privacy Policy</a>.
                                                        </span>
                                                    </template>
                                                </v-checkbox>
                                            </div>
                                        </transition>
                                    </template>
                                </form-demo>
                            </v-col>
                        </v-row>
                    </div>

                    <thank-you-demo
                        v-else-if="thank_you_shown && !double_opt_in_demo_shown"
                        :form_data="form_data"
                        has_flow_demo_after
                        @show-demo="double_opt_in_demo_shown = true"
                    />

                    <flow-demo
                        v-else-if="double_opt_in_demo_shown"
                        :form_data="form_data"
                        :conversation="conversation"
                        @reset-form="backToForm"
                    >
                        <template #technical-notes>
                            <ul>
                                <li v-if="form_data.phone && form_data.phone_consent">
                                    <strong>Double Opt-In Timeout:</strong> If the customer doesn't reply "YES"
                                    within 1 day, it's treated the same as texting "STOP"
                                </li>
                                <li v-if="form_data.phone && form_data.phone_consent">
                                    <strong>Existing Customers:</strong> The double opt-in message will NOT be sent
                                    to customers who are already opted in - they'll only receive a thank you message
                                </li>
                                <li>
                                    <strong>Customer Creation:</strong> A new customer will be automatically created
                                    in Commerce7 if one doesn't already exist
                                </li>
                                <li v-if="form_data.email && form_data.email_consent">
                                    <strong>Email Consent:</strong> Email consent is automatically recorded in
                                    Commerce7
                                </li>
                                <li>
                                    <strong>Tagging:</strong> RedChirp can apply custom tags to customers that check
                                    in for easy identification and marketing segmentation
                                </li>
                            </ul>
                        </template>
                    </flow-demo>
                </div>
            </transition>

            <placeholder-dialog ref="placeholderDialog" />
        </div>
    </v-app>
</template>

<script>
import BackdropDemo from '@/components/demos/shared/backdrop.vue';
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import FormDemo from '@/components/demos/guest-checkin/form-demo.vue';
import ThankYouDemo from '@/components/demos/guest-checkin/thank-you-demo.vue';
import FlowDemo from '@/components/demos/guest-checkin/flow-demo.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';
import Validate from '@/js/validate.js';

export default {
    components: {
        BackdropDemo,
        BrandingHeader,
        FormDemo,
        ThankYouDemo,
        FlowDemo,
        PlaceholderDialog
    },

    data() {
        return {
            submitted: false,
            thank_you_shown: false,
            double_opt_in_demo_shown: false,
            form_data: {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                email_consent: false,
                phone: '',
                phone_consent: false,
            },
            rules: {
                required: value => !!value || 'This field is required',
                email_verification: value => {
                    if (!Validate.isEmailAddress(value?.trim())) {
                        return 'Please enter a valid email address';
                    }
                    return true;
                },
                optional_phone_verification: value => {
                    if (!value || value.trim() === '') {
                        return true;
                    }

                    if (!Validate.isUSPhoneNumber(value?.trim())) {
                        return 'A valid phone number is required';
                    }
                    return true;
                },
            }
        }
    },

    computed: {
        conversation() {
            return [
                {
                    type: 'company',
                    text: 'Reply YES to receive messages with info, promotions and offers. We may use automation to send these texts. Consent is not a condition of purchase. Texts and calls may be recorded. Msg frequency varies. Msg and/or data rates may apply.',
                    sender: 'Barrel Creek Vineyards'
                },
                {
                    type: 'customer',
                    text: 'YES',
                    sender: this.form_data.first_name
                },
                {
                    type: 'company',
                    text: 'Thanks! You\'re now subscribed to receive updates from Barrel Creek Vineyards. Text STOP to opt out or HELP for help at any time.',
                    sender: 'Barrel Creek Vineyards'
                }
            ];
        }
    },

    methods: {
        onInputPhone(value) {
            const updated = { ...this.form_data, phone: this.$rc.maskPhoneNumber(value) };
            this.form_data = updated;
        },

        submitForm() {
            if (this.$refs.form?.validate()) {
                this.submitted = true;
                this.showThankYou();
            }
        },

        showThankYou() {
            this.scrollToTop();
            this.thank_you_shown = true;
        },

        resetForm() {
            this.form_data = {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                phone: ''
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        backToForm() {
            this.resetForm();
            this.submitted = false;
            this.scrollToTop();
            this.thank_you_shown = false;
            this.double_opt_in_demo_shown = false;
        },

        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },

    mounted() {
        document.title = "Guest Check-In | Conservative";
    }

}
</script>

<style lang="scss" scoped>
.demo-wrapper {
    --brand-primary: #40728F;
    --v-primary-base: var(--brand-primary);
    --v-anchor-base: var(--brand-primary);
    --max-width: 1200px;

    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    .side-by-side-image-wrapper {
        position: relative;
        overflow: hidden;
    }

    .side-by-side-image {
        object-fit: cover;
        object-position: right;
        max-width: 100%;
        width: 100%;
        height: 100%;
        border-radius: 0.5rem;
        transition: all 0.3s ease-in-out;
    }

    .heading {
        font-size: 1.5rem;
        font-weight: 600;
        margin-top: 0.5rem;
        margin-bottom: 0.25rem;
        color: var(--v-primary-base);
    }

    .subheading {
        font-size: 1.2rem;
        font-weight: 500;
        margin-bottom: 1.5rem;
        color: #666666;
    }

    .form-container {
        position: relative;
        max-width: min(var(--max-width), 90%);
        margin: 2rem auto;
        padding: 2rem;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 1rem;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-top: 4px solid var(--v-primary-base);
    }

    .disclaimer {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        font-size: 1rem; // must be same size as input labels
    }

    :deep(.v-input--selection-controls__input) {
        align-self: start;
    }
}
</style>